import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr, clearToken, uploadImage } from "../lib/api";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  LogOut,
  Search,
  Image as ImageIcon,
  Save,
  Loader2,
} from "lucide-react";
import { categories } from "../data/products";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/files/") || u.startsWith("/uploads/")) return `${BACKEND}${u}`;
  return u;
};

const EMPTY = {
  name: "",
  category: "Biscuiți",
  size: "",
  pack: "",
  price: "",
  description: "",
  image: "",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, product = edit
  const [search, setSearch] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setProducts(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
        await loadAll();
      } catch {
        clearToken();
        navigate("/admin/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q)
    );
  }, [products, search]);

  const onLogout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  const onSaved = async () => {
    setEditing(null);
    await loadAll();
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Ștergi „${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Produs șters");
      await loadAll();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#080706] text-[#FDF8F5]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#080706]/95 backdrop-blur-xl border-b border-[#1F1A18]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#C8A97E]">
              — Panou Admin
            </p>
            <h1 className="font-[Playfair_Display] text-xl tracking-tight">
              Produse Alimentare <em className="italic text-[#C8A97E]">En Gross</em>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-[#9D8B7B]">{me?.email}</span>
            <a
              href="/"
              className="text-xs uppercase tracking-[0.18em] text-[#9D8B7B] hover:text-[#C8A97E] transition-colors px-3 py-2 border border-[#2A2522] hover:border-[#C8A97E]"
            >
              Site public
            </a>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] bg-[#C8A97E] text-[#080706] px-4 py-2.5 hover:bg-[#B3956D] transition-colors"
            >
              <LogOut size={14} strokeWidth={1.4} /> Ieșire
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        {/* Stats + actions */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <h2 className="font-[Playfair_Display] text-4xl">
              Produse <em className="italic text-[#C8A97E]">({products.length})</em>
            </h2>
            <p className="text-[#9D8B7B] text-sm font-light mt-2">
              Gestionează catalogul — adaugă, editează sau șterge produse.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-72">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9D8B7B]"
                size={16}
                strokeWidth={1.4}
              />
              <input
                data-testid="admin-search"
                placeholder="Caută produs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-[#0F0D0B] border border-[#2A2522] focus:border-[#C8A97E] focus:outline-none pl-10 pr-4 py-3 text-sm"
              />
            </div>
            <button
              data-testid="admin-add-product"
              onClick={() => setEditing({ ...EMPTY })}
              className="flex items-center justify-center gap-2 bg-[#C8A97E] text-[#080706] px-5 py-3 text-sm font-medium tracking-wide hover:bg-[#B3956D] transition-colors"
            >
              <Plus size={16} strokeWidth={1.8} /> Adaugă produs
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="border border-[#1F1A18] overflow-hidden" data-testid="admin-products-table">
          <div className="hidden md:grid grid-cols-12 gap-4 bg-[#0F0D0B] px-5 py-4 text-[10px] uppercase tracking-[0.2em] text-[#9D8B7B] border-b border-[#1F1A18]">
            <div className="col-span-1">Poză</div>
            <div className="col-span-4">Nume</div>
            <div className="col-span-2">Categorie</div>
            <div className="col-span-1">Gramaj</div>
            <div className="col-span-1">Ambalaj</div>
            <div className="col-span-1">Preț</div>
            <div className="col-span-2 text-right">Acțiuni</div>
          </div>

          {loading ? (
            <div className="py-20 flex items-center justify-center text-[#9D8B7B]">
              <Loader2 className="animate-spin mr-2" size={18} /> Se încarcă...
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-[#9D8B7B]">Niciun produs.</div>
          ) : (
            <ul>
              {filtered.map((p) => (
                <li
                  key={p.id}
                  data-testid={`admin-row-${p.id}`}
                  className="grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-4 items-center px-5 py-4 border-b border-[#1F1A18] hover:bg-[#0F0D0B]/50 transition-colors"
                >
                  <div className="col-span-2 md:col-span-1">
                    <Thumb src={p.image} alt={p.name} />
                  </div>
                  <div className="col-span-2 md:col-span-4">
                    <p className="text-sm font-[Playfair_Display] leading-snug">{p.name}</p>
                    {p.description && (
                      <p className="text-xs text-[#9D8B7B] mt-1 line-clamp-1 font-light">
                        {p.description}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#C8A97E] bg-[#0F0D0B] border border-[#2A2522] px-2 py-1">
                      {p.category}
                    </span>
                  </div>
                  <div className="md:col-span-1 text-sm font-light text-[#C8B9A8]">
                    {p.size || "—"}
                  </div>
                  <div className="md:col-span-1 text-sm font-light text-[#C8B9A8]">
                    {p.pack || "—"}
                  </div>
                  <div className="md:col-span-1 text-sm font-medium text-[#C8A97E]">
                    {p.price != null ? `${p.price} lei` : "—"}
                  </div>
                  <div className="md:col-span-2 flex items-center justify-end gap-2">
                    <button
                      data-testid={`admin-edit-${p.id}`}
                      onClick={() => setEditing(p)}
                      className="p-2 border border-[#2A2522] hover:border-[#C8A97E] hover:text-[#C8A97E] transition-colors"
                      title="Editează"
                    >
                      <Pencil size={14} strokeWidth={1.4} />
                    </button>
                    <button
                      data-testid={`admin-delete-${p.id}`}
                      onClick={() => onDelete(p)}
                      className="p-2 border border-[#2A2522] hover:border-red-500 hover:text-red-400 transition-colors"
                      title="Șterge"
                    >
                      <Trash2 size={14} strokeWidth={1.4} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      {editing && (
        <ProductDrawer
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

const Thumb = ({ src, alt }) => {
  if (!src) {
    return (
      <div className="w-14 h-14 bg-[#0F0D0B] border border-[#2A2522] flex items-center justify-center">
        <ImageIcon size={16} strokeWidth={1.2} className="text-[#3D3530]" />
      </div>
    );
  }
  return (
    <div className="w-14 h-14 bg-white border border-[#2A2522] overflow-hidden">
      <img src={absUrl(src)} alt={alt} className="w-full h-full object-cover" />
    </div>
  );
};

const ProductDrawer = ({ product, onClose, onSaved }) => {
  const isEdit = !!product.id;
  const [form, setForm] = useState({
    name: product.name || "",
    category: product.category || "Biscuiți",
    size: product.size || "",
    pack: product.pack || "",
    price: product.price ?? "",
    description: product.description || "",
    image: product.image || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, image: url }));
      toast.success("Imagine încărcată");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploading(false);
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.category.trim()) {
      toast.error("Numele și categoria sunt obligatorii.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price:
        form.price === "" || form.price === null ? null : Number(form.price),
    };
    try {
      if (isEdit) {
        await api.put(`/products/${product.id}`, payload);
        toast.success("Produs actualizat");
      } else {
        await api.post(`/products`, payload);
        toast.success("Produs adăugat");
      }
      onSaved();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="admin-product-drawer"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSave}
        className="w-full max-w-lg h-full bg-[#0A0908] border-l border-[#1F1A18] overflow-y-auto"
      >
        <div className="sticky top-0 bg-[#0A0908] border-b border-[#1F1A18] flex items-center justify-between px-6 py-4 z-10">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#C8A97E]">
              {isEdit ? "Editare" : "Adăugare"}
            </p>
            <h3 className="font-[Playfair_Display] text-lg mt-1">
              {isEdit ? "Modifică produs" : "Produs nou"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 border border-[#2A2522] hover:border-[#C8A97E] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Image */}
          <div>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#C8A97E]">
              Imagine
            </span>
            <div className="mt-3 flex items-center gap-4">
              <div className="w-24 h-24 bg-white border border-[#2A2522] overflow-hidden flex items-center justify-center">
                {form.image ? (
                  <img src={absUrl(form.image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="text-[#3D3530]" size={22} strokeWidth={1.2} />
                )}
              </div>
              <div className="flex-1">
                <label className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] border border-[#2A2522] hover:border-[#C8A97E] px-4 py-2.5 cursor-pointer">
                  {uploading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ImageIcon size={14} strokeWidth={1.4} />
                  )}
                  {uploading ? "Se încarcă..." : "Încarcă imagine"}
                  <input
                    data-testid="admin-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={onUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
                <input
                  type="text"
                  value={form.image}
                  onChange={upd("image")}
                  placeholder="sau lipește URL..."
                  className="w-full mt-3 bg-transparent border-b border-[#3D3530] py-2 text-xs focus:border-[#C8A97E] focus:outline-none"
                />
              </div>
            </div>
          </div>

          <Field label="Titlu *" testId="admin-field-name">
            <input
              data-testid="admin-input-product-name"
              required
              value={form.name}
              onChange={upd("name")}
              className="w-full bg-transparent border-b border-[#3D3530] py-3 focus:border-[#C8A97E] focus:outline-none"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Categorie *" testId="admin-field-category">
              <select
                data-testid="admin-input-category"
                value={form.category}
                onChange={upd("category")}
                className="w-full bg-[#0A0908] border-b border-[#3D3530] py-3 focus:border-[#C8A97E] focus:outline-none text-[#FDF8F5]"
              >
                {categories
                  .filter((c) => c !== "Toate")
                  .map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Preț (lei)" testId="admin-field-price">
              <input
                data-testid="admin-input-price"
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={upd("price")}
                placeholder="ex: 12.50"
                className="w-full bg-transparent border-b border-[#3D3530] py-3 focus:border-[#C8A97E] focus:outline-none"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Gramaj / Volum">
              <input
                data-testid="admin-input-size"
                value={form.size}
                onChange={upd("size")}
                placeholder="ex: 500 g"
                className="w-full bg-transparent border-b border-[#3D3530] py-3 focus:border-[#C8A97E] focus:outline-none"
              />
            </Field>
            <Field label="Ambalaj">
              <input
                data-testid="admin-input-pack"
                value={form.pack}
                onChange={upd("pack")}
                placeholder="ex: 6/bax"
                className="w-full bg-transparent border-b border-[#3D3530] py-3 focus:border-[#C8A97E] focus:outline-none"
              />
            </Field>
          </div>

          <Field label="Descriere">
            <textarea
              data-testid="admin-input-description"
              value={form.description}
              onChange={upd("description")}
              rows={4}
              placeholder="Descriere produs..."
              className="w-full bg-transparent border-b border-[#3D3530] py-3 focus:border-[#C8A97E] focus:outline-none resize-none"
            />
          </Field>
        </div>

        <div className="sticky bottom-0 bg-[#0A0908] border-t border-[#1F1A18] px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-[0.18em] border border-[#2A2522] hover:border-[#C8A97E] px-5 py-3"
          >
            Anulează
          </button>
          <button
            type="submit"
            data-testid="admin-save-product"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#C8A97E] text-[#080706] px-6 py-3 text-xs uppercase tracking-[0.18em] font-medium hover:bg-[#B3956D] transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isEdit ? "Salvează" : "Adaugă"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, children, testId }) => (
  <label data-testid={testId} className="block">
    <span className="text-[10px] uppercase tracking-[0.25em] text-[#C8A97E]">{label}</span>
    {children}
  </label>
);
